"use client"

import React, { useState } from "react"

import { ExtendedAddressRO } from "@/lib/storefront/addresses/address"
import { CheckoutContext } from "@/lib/storefront/checkout/checkout-service"
import AddAddressButton from "@/components/common/addresses/AddAddressButton"
import AddressEditModal from "@/components/common/addresses/AddressEditModal"
import SelectAddress from "@/components/common/addresses/SelectAddress"
import { UbCheckoutAddressesViewStoryblok } from "@/components/storyblok/component-types"

interface CheckoutAddressesProps {
  blok: UbCheckoutAddressesViewStoryblok
  checkoutContext: CheckoutContext
}

export default function CheckoutAddressesClient({ blok, checkoutContext }: CheckoutAddressesProps) {
  const [invoiceToAddressToEdit, setInvoiceToAddressToEdit] = useState<ExtendedAddressRO>()
  const [shipToAddressToEdit, setShipToAddressToEdit] = useState<ExtendedAddressRO>()
  const [invoiceToAddressEditVisible, setInvoiceToAddressEditVisible] = useState(false)
  const [shipToAddressEditVisible, setShipToAddressEditVisible] = useState(false)

  const addresses = checkoutContext.addresses
  const invoiceToAddressesInit = addresses.filter((address) => address.invoiceToAddress)
  const shipToAddressesInit = addresses.filter((address) => address.shipToAddress)
  const preferredInvoiceToAddress = checkoutContext.userData?.preferredInvoiceToAddress ?? invoiceToAddressesInit[0]
  const preferredShipToAddress = checkoutContext.userData?.preferredShipToAddress ?? shipToAddressesInit[0]
  const invoiceToAddressesSorted = invoiceToAddressesInit.sort((a, b) =>
    b.id === preferredInvoiceToAddress.id ? 1 : -1
  )
  const shipToAddressesSorted = shipToAddressesInit.sort((a, b) => (b.id === preferredShipToAddress.id ? 1 : -1))

  // Add local state for addresses and setters
  const [invoiceToAddresses, setInvoiceToAddresses] = useState<ExtendedAddressRO[]>(invoiceToAddressesSorted)
  const [shipToAddresses, setShipToAddresses] = useState<ExtendedAddressRO[]>(shipToAddressesSorted)

  const handleEditInvoiceToAddress = (address: ExtendedAddressRO) => {
    setInvoiceToAddressToEdit(address)
    setInvoiceToAddressEditVisible(true)
  }

  const handleEditShipToAddress = (address: ExtendedAddressRO) => {
    setShipToAddressToEdit(address)
    setShipToAddressEditVisible(true)
  }

  const handleSaveInvoiceToAddress = () => {}
  const handleSaveShipToAddress = () => {}

  return (
    <>
      <div className={"flex w-full flex-col gap-10"}>
        <div className={"flex flex-col gap-2"}>
          <div className={"flex w-full flex-row flex-wrap items-center justify-between gap-2"}>
            <h2 className={"text-lg font-bold"}>{blok.title_invoice_to_address}</h2>

            <AddAddressButton
              customer={checkoutContext.customerData}
              user={checkoutContext.userData}
              onAdd={(address, user) => setInvoiceToAddresses((prev) => [...prev, address])}
            />
          </div>
          <SelectAddress
            initialAddress={preferredInvoiceToAddress}
            addresses={invoiceToAddresses}
            setAddresses={setInvoiceToAddresses}
            onEdit={handleEditInvoiceToAddress}
            customer={checkoutContext.customerData}
            user={checkoutContext.userData}
          />
        </div>
        <div className={"flex flex-col gap-2"}>
          <div className={"flex w-full flex-row flex-wrap items-center justify-between gap-2"}>
            <h2 className={"text-lg font-bold"}>{blok.title_ship_to_address}</h2>
            <AddAddressButton
              customer={checkoutContext.customerData}
              user={checkoutContext.userData}
              onAdd={(address, user) => setShipToAddresses((prev) => [...prev, address])}
            />
          </div>
          <SelectAddress
            initialAddress={preferredShipToAddress}
            addresses={shipToAddresses}
            setAddresses={setShipToAddresses}
            onEdit={handleEditShipToAddress}
            customer={checkoutContext.customerData}
            user={checkoutContext.userData}
          />
        </div>
      </div>

      <AddressEditModal
        visible={invoiceToAddressEditVisible}
        address={invoiceToAddressToEdit}
        customer={checkoutContext.customerData}
        user={checkoutContext.userData}
        onCancel={() => setInvoiceToAddressEditVisible(false)}
        mode="edit"
      />

      <AddressEditModal
        visible={shipToAddressEditVisible}
        address={shipToAddressToEdit}
        customer={checkoutContext.customerData}
        user={checkoutContext.userData}
        onCancel={() => setShipToAddressEditVisible(false)}
        mode="edit"
      />
    </>
  )
}
